<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { stripWahaSendPrefix } from 'dashboard/helper/wahaSendPrefix';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack, useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { labelForToolTrace } from 'shared/helpers/captainToolLabels';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { isWorkspaceV2Path } from 'dashboard/helper/workspaceV2';
import { contaLocalStorage } from 'dashboard/helper/accountScopedStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import {
  inboxSupportsMessageReactions,
  isWahaApiInbox,
} from 'dashboard/helper/wahaInbox';
import { isWithinWhatsappEditWindow } from 'dashboard/helper/wahaMessageEdit';
import { parseAdMessage } from 'dashboard/helper/adMessage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import KanbanMessageCards from 'dashboard/routes/dashboard/internal-chat/components/KanbanMessageCards.vue';
import TextBubble from './bubbles/Text/Index.vue';
import AdMessageBubble from './bubbles/AdMessage.vue';
import LeadFormMessageBubble from './bubbles/LeadFormMessage.vue';
import PollBubble from './bubbles/Poll.vue';
import ListBubble from './bubbles/List.vue';
import EventBubble from './bubbles/Event.vue';
import PixBubble from './bubbles/Pix.vue';
import StatusReplyBubble from './bubbles/StatusReply.vue';
import AlbumBubble from './bubbles/Album.vue';
import { isAlbumAnnouncement } from 'dashboard/helper/albumGrouping';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import FallbackBubble from './bubbles/Fallback.vue';
import WhatsAppTemplateBubble from './bubbles/WhatsAppTemplate.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import PaymentBubble from './bubbles/Payment.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
import AiCenterPrivateNote from './AiCenterPrivateNote.vue';
import { aiCenterNoteItem } from './aiCenterNote';

import MessageError from './MessageError.vue';
import MessageMeta from './MessageMeta.vue';
import MessageReactions from './MessageReactions.vue';
import MessageReactionPicker from './MessageReactionPicker.vue';
import MessageApi from 'dashboard/api/inbox/message';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useBranding } from 'shared/composables/useBranding';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  albumAttachments: { type: Array, default: () => [] }, // eslint-disable-line vue/no-unused-properties
  call: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

// W2 — reações. Declarado aqui em cima de propósito: `showMetaArea`, mais
// abaixo, precisa enxergar `hasReactions`, e const em <script setup> tem TDZ —
// um computed que referencia algo ainda não declarado quebra o setup inteiro
// (foi o que matou o card do Kanban uma vez).
const AGENT_AUTHOR = 'agent';
const localReactions = ref(null);
const reactions = computed(
  () => localReactions.value ?? props.contentAttributes?.reactions ?? {}
);
const hasReactions = computed(() => Object.keys(reactions.value).length > 0);

// M3/M4 — apagada/editada: filete + selo + original inline vivem em
// bubbles/Base.vue + bubbles/MessageLifecycle.vue (texto sempre preservado).

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const { replaceInstallationName } = useBranding();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
// SiteUp: mensagens enviadas pelo celular (app WAHA) ou por API entram no
// ChatWoot como nota privada — pra não serem reenviadas ao cliente — mas do
// ponto de vista de quem lê a conversa elas SAÍRAM. Os prefixos abaixo são os
// mesmos gravados pelo backend (Waha::SourceIdResolver), então pintamos como
// mensagem enviada; nota privada de verdade continua âmbar.
const EXTERNAL_SEND_PREFIX = /^\*📱 Enviado (do WhatsApp|via API)\*/;

// W5 (Lucas 2026-08-10): o prefixo `*📱 Enviado do WhatsApp*` / `*📱 Enviado via
// API*` NÃO é escrito por nós — vem no corpo da mensagem pelo plugin "ChatWoot
// App" embutido no WAHA. Some da TELA aqui; o dado no banco fica intacto (nada
// se perde, e é reversível). A correção na origem é na imagem do WAHA, que é
// nossa — mas ela não limpa histórico, por isso este strip existe.

const isExternalSend = computed(
  () => props.private && EXTERNAL_SEND_PREFIX.test((props.content || '').trim())
);

// Origem do envio pra etiqueta da linha da hora: 'mobile' (espelho do app),
// 'api' (automação/integração) ou null (envio normal pelo painel).
// Conteúdo COMO APARECE. O `props.content` cru continua sendo a fonte pra
// detectar a origem (abaixo) e pro menu de contexto — trocar os dois quebraria
// o selo "Pelo celular" que substitui o prefixo.
const displayContent = computed(() => stripWahaSendPrefix(props.content));

const sendOrigin = computed(() => {
  // Só faz sentido pra SAÍDA: entrada de webhook também nasce "via API" no
  // backend (WAHA), mas pro leitor é só a mensagem do cliente.
  const outgoingTypes = [MESSAGE_TYPES.OUTGOING, MESSAGE_TYPES.TEMPLATE];
  if (!outgoingTypes.includes(props.messageType)) return null;
  const trimmed = (props.content || '').trim();
  if (/^\*📱 Enviado do WhatsApp\*/.test(trimmed)) return 'mobile';
  if (/^\*📱 Enviado via API\*/.test(trimmed)) return 'api';
  if (props.contentAttributes?.createdViaApi) return 'api';
  return null;
});

const variant = computed(() => {
  if (props.private && !isExternalSend.value) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  // Ação pro cliente (botões, lista, CSAT, form, pagamento): bolha neutra,
  // o mesmo idioma do cartão de anúncio. Texto comum do agente continua azul.
  const isCustomerAction =
    props.contentType === CONTENT_TYPES.INPUT_CSAT ||
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(
      props.contentType
    ) ||
    Boolean(props.contentAttributes?.payment?.link);
  if (isCustomerAction && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.USER;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

// SiteUp: mensagem gerada pela IA (Captain ou bot do AiAgents). Libera o
// "Reportar erro da IA" no menu de contexto só nas respostas da IA.
const isAiMessage = computed(() => {
  const senderType = props.sender?.type ?? props.senderType;
  return (
    props.messageType === MESSAGE_TYPES.OUTGOING &&
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  );
});

// SiteUp (#5 trace): ferramentas que a IA usou nesta resposta (gravadas em
// content_attributes.captain_tool_calls/results pelo ResponseBuilderJob).
// Rodapé discreto sob a bolha — com código/campo quando a tool falhou.
const aiToolCalls = computed(() => {
  const calls = props.contentAttributes?.captainToolCalls;
  const results = props.contentAttributes?.captainToolResults;
  const labels = labelForToolTrace(calls, results);
  return labels;
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

// SiteUp V2 (F1.7): a linha da hora saiu de dentro da bolha (onde o cinza
// neutro morria em cima do azul/iris) e virou uma linha própria embaixo dela.
// O balão de chamada de voz nunca mostrou hora — antes via `hide-meta` no
// BaseBubble; agora a decisão mora aqui, junto com as outras.
const hideMessageMeta = computed(
  () => props.contentType === CONTENT_TYPES.VOICE_CALL
);

const showMessageMeta = computed(() => {
  if (hideMessageMeta.value) return false;
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) return false;

  // Em mensagens seguidas do mesmo remetente, só a última mostra a hora.
  return !shouldGroupWithNext.value;
});

// Tudo que fica abaixo da bolha divide a mesma [grid-area:meta]. Sem um
// container em coluna esses irmãos caem na MESMA célula do grid e se
// sobrepõem. O v-if evita que a linha vazia ocupe altura e afrouxe o
// agrupamento de mensagens seguidas.
const showMetaArea = computed(
  () =>
    showMessageMeta.value ||
    !!props.contentAttributes.externalError ||
    hasReactions.value ||
    (isAiMessage.value && aiToolCalls.value.length > 0)
);

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentAttributes?.payment?.link) {
    return PaymentBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  // SiteUp (W4/M1): cartão de enquete desde o envio. O backend grava
  // content_attributes.poll.options na chegada (PollAttributeAttach); sem
  // opções reconhecidas a mensagem segue como texto — nunca cartão vazio.
  const pollOptions = props.contentAttributes?.poll?.options;
  if (Array.isArray(pollOptions) && pollOptions.length > 0) {
    return PollBubble;
  }

  // SiteUp (M6/M7): lista interativa e evento. O backend já deixou
  // content_attributes.list / .event prontos (texto parseado ou estruturado
  // do WAHA). Sem parse no front.
  if (props.contentAttributes?.list?.items?.length) {
    return ListBubble;
  }
  if (props.contentAttributes?.event?.name) {
    return EventBubble;
  }
  // SiteUp (M9): álbum — anúncio com content_attributes.album, ou o texto
  // histórico do WAHA (álbuns já no banco sem o carimbo).
  if (
    props.contentAttributes?.album ||
    isAlbumAnnouncement({
      content: props.content,
      contentAttributes: props.contentAttributes,
    })
  ) {
    return AlbumBubble;
  }
  // SiteUp (M8/M10): Pix e resposta a status — só com content_attributes
  // estruturado do backend. Nada de parse no front.
  if (props.contentAttributes?.pix) {
    return PixBubble;
  }
  if (props.contentAttributes?.statusReply) {
    return StatusReplyBubble;
  }
  // SiteUp: localização e contato espelhados chegam como texto do WAHA; o
  // backend carimba content_attributes.location / shared_contacts na criação
  // e aqui viram os MESMOS cards do caminho de anexo.
  if (props.contentAttributes?.location?.latitude !== undefined) {
    return LocationBubble;
  }
  if (props.contentAttributes?.sharedContacts?.length) {
    return ContactBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  // Template da API oficial (campanha/agendamento/manual): mockup completo
  // com header, rodapé e botões — o fallback pra texto vive dentro da bolha.
  if (
    props.additionalAttributes?.templateParams?.name &&
    [MESSAGE_TYPES.OUTGOING, MESSAGE_TYPES.TEMPLATE].includes(props.messageType)
  ) {
    return WhatsAppTemplateBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (fileType === ATTACHMENT_TYPES.FALLBACK) return FallbackBubble;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  // SiteUp: chegada de lead de anúncio que o bridge manda como markdown cru —
  // 765 caracteres em média de peça publicitária cobrindo o que o cliente
  // escreveu. Vira card, com a fala da pessoa em destaque.
  //
  // Anexo e tradução ficam FORA: o TextBubble já sabe renderizar os dois
  // (AttachmentChips/TranslationToggle) e duplicar isso aqui só criaria uma
  // segunda implementação para esquecer de manter. Nesses casos o texto do
  // anúncio continua cru — feio, mas nada some.
  //
  // parseAdMessage devolve null a qualquer sinal de que esconder o original
  // perderia conteúdo (linha não reconhecida, preâmbulo, campo novo do bridge).
  //
  // O gatilho tem DUAS fontes, nesta ordem:
  //
  // 1. externalAdReply na própria mensagem — o que o fork WAHA (waha#3) passou
  //    a mandar. Aqui o `content` é só a fala do cliente, então o parser de
  //    texto devolveria null e o card nunca apareceria. Por isso o carimbo
  //    estruturado precisa contar como gatilho por si só.
  // 2. o parser de texto, para o despejo cru — as 3.497 mensagens já no banco
  //    e qualquer bridge que não passe pelo caminho estruturado.
  // SiteUp: a thumbnail do criativo chega como anexo de imagem junto do
  // despejo do anúncio (146/146 nos últimos 21 dias na conta 11) e o gatilho
  // antigo — sem anexo — suprimia o card exatamente nesses casos. Um anexo
  // único de imagem vira o criativo dentro do próprio card; qualquer outro
  // anexo segue no TextBubble, que já sabe renderizar tudo.
  // SiteUp: nota do formulário de lead vem com as respostas estruturadas. Vale
  // antes do card de anúncio porque as duas podem coexistir na mesma conversa
  // (lead veio do anúncio E respondeu o formulário) e são mensagens distintas.
  if (props.contentAttributes?.leadForm?.fields?.length) {
    return LeadFormMessageBubble;
  }

  const adCreativeOnly =
    !props.attachments?.length ||
    (props.attachments.length === 1 &&
      props.attachments[0].fileType === ATTACHMENT_TYPES.IMAGE);
  if (
    adCreativeOnly &&
    !props.contentAttributes?.translations &&
    (props.contentAttributes?.externalAdReply || parseAdMessage(props.content))
  ) {
    return AdMessageBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

// Nota da Central da IA: só o cartão compacto, sem o corpo amarelo repetido.
const aiCenterNote = computed(() =>
  aiCenterNoteItem(props.contentAttributes, props.private)
);

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

// W2 — reações. O mapa vem do backend em content_attributes.reactions e é
// espelhado localmente: o websocket traz a versão do servidor, mas o clique
// precisa responder na hora.
const isReacting = ref(false);

// WAHA e WhatsApp Cloud oficial. Sem source_id não há o que reagir no provedor.
const canReact = computed(
  () =>
    Boolean(props.sourceId) &&
    isBubble.value &&
    !isMessageDeleted.value &&
    props.status !== MESSAGE_STATUS.FAILED &&
    inboxSupportsMessageReactions(inbox.value)
);

const agentReactionEmoji = computed(() => {
  const found = Object.entries(reactions.value).find(([, authors]) =>
    (Array.isArray(authors) ? authors : []).includes(AGENT_AUTHOR)
  );
  return found ? found[0] : '';
});

const sendReaction = async emoji => {
  if (isReacting.value) return;
  isReacting.value = true;
  const previous = localReactions.value;
  try {
    const { data } = await MessageApi.react(
      props.conversationId,
      props.id,
      emoji
    );
    localReactions.value = data?.reactions ?? {};
  } catch (error) {
    localReactions.value = previous;
    useAlert(t('CONVERSATION.REACTIONS.FAILED'));
  } finally {
    isReacting.value = false;
  }
};

// Clicar no próprio emoji remove; clicar em outro troca (uma reação por
// pessoa, igual ao WhatsApp).
const onToggleReaction = emoji => {
  sendReaction(agentReactionEmoji.value === emoji ? '' : emoji);
};

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
    source_id: props.sourceId,
    created_at: props.createdAt,
  };
});

// Espelha Waha::EditMessageService::EDIT_WINDOW_MINUTES = 15.
const isWithinEditWindow = computed(() =>
  isWithinWhatsappEditWindow(props.createdAt)
);

// Inbox WAHA real (Channel::Api + waha_session_name). API genérica e
// WhatsApp Cloud oficial NÃO devem ganhar edição remota inexistente.
// Backend também valida no PATCH via Waha::EditMessageService.
const isWahaInbox = computed(() => isWahaApiInbox(inbox.value));

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    // Edicao so permitida em mensagens outgoing, com texto, sem anexo,
    // dentro de 15min do envio (limite WhatsApp), e em inbox WAHA
    // (Channel::Api). A propagacao pro WhatsApp acontece via WAHA edit.
    edit:
      isOutgoing &&
      hasText &&
      !hasAttachments &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value &&
      isWithinEditWindow.value &&
      isWahaInbox.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    forward: !isFailedOrProcessing && !isMessageDeleted.value,
    report: isAiMessage.value,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

const messageContextMenu = ref(null);

// Hover reveal only on fine pointers that can hover (mouse).
// Touch (PWA iPhone) keeps the icons visible so nothing is trapped.
const hoverHideOnFinePointerClass = [
  '[@media(hover:hover)_and_(pointer:fine)]:opacity-0',
  '[@media(hover:hover)_and_(pointer:fine)]:group-hover/msg:opacity-100',
  '[@media(hover:hover)_and_(pointer:fine)]:group-focus-within/msg:opacity-100',
].join(' ');

const hoverRevealClass = [
  hoverHideOnFinePointerClass,
  '[@media(hover:hover)_and_(pointer:fine)]:pointer-events-none',
  '[@media(hover:hover)_and_(pointer:fine)]:group-hover/msg:pointer-events-auto',
  '[@media(hover:hover)_and_(pointer:fine)]:group-focus-within/msg:pointer-events-auto',
  '[@media(hover:hover)_and_(pointer:fine)]:focus-visible:opacity-100',
  '[@media(hover:hover)_and_(pointer:fine)]:focus-visible:pointer-events-auto',
].join(' ');

const quickActionButtonClass = [
  'flex size-9 shrink-0 items-center justify-center rounded-full',
  'border border-n-weak bg-n-solid-2 text-n-slate-11',
  'transition-opacity duration-150',
  'hover:bg-n-alpha-2 hover:text-n-slate-12',
  hoverRevealClass,
].join(' ');

const forceVisibleClass = '!opacity-100 pointer-events-auto';

const quickActionOrderClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? 'order-first' : 'order-last'
);

// A bolha de audio e uma faixa de CONTROLES: play na esquerda, velocidade,
// mudo e download na direita. A seta do menu, absoluta no canto, caia em cima
// do ultimo controle e a atendente clicava no menu querendo o player (relato
// da Adala, 24/08). Nessa bolha a seta nao aparece; responder, reagir e
// encaminhar continuam do lado, e o menu completo segue no botao direito.
const isAudioControlBubble = computed(
  () => componentToRender.value === AudioBubble
);

const shouldShowHoverChevron = computed(
  () =>
    shouldShowContextMenu.value && isBubble.value && !isAudioControlBubble.value
);

// SiteUp: a setinha mora SEMPRE no canto direito da bolha (como no
// WhatsApp); os atalhos de acao ficam do outro lado, sem disputa de canto.
const chevronCornerClass = 'ltr:right-0 rtl:left-0';

const solidBubbleVariants = [
  MESSAGE_VARIANTS.AGENT,
  MESSAGE_VARIANTS.USER,
  MESSAGE_VARIANTS.BOT,
  MESSAGE_VARIANTS.TEMPLATE,
];

const chevronFadeClass = computed(() => {
  if (!solidBubbleVariants.includes(variant.value)) return '';

  const fromColor =
    variant.value === MESSAGE_VARIANTS.AGENT
      ? 'from-n-blue-9'
      : 'from-n-slate-4';
  const direction = 'ltr:bg-gradient-to-l rtl:bg-gradient-to-r';

  return `${fromColor} ${direction}`;
});

const chevronIconClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.AGENT ? 'text-white' : 'text-n-slate-11'
);

const chevronButtonSurfaceClass = computed(() =>
  chevronFadeClass.value ? '' : 'rounded-full bg-n-solid-2/90'
);

const chevronRadiusClass = 'ltr:rounded-tr-[20px] rtl:rounded-tl-[20px]';

function openContextMenuFromChevron(e) {
  e.preventDefault();
  e.stopPropagation();
  const rect = e.currentTarget.getBoundingClientRect();
  contextMenuPosition.value = {
    x: rect.left,
    y: rect.bottom,
  };
  showContextMenu.value = true;
  useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
}

function openForwardModal() {
  messageContextMenu.value?.showForwardModal();
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  // Indexado por display_id da conversa, que repete entre contas.
  contaLocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  content: displayContent,
  isPrivate: computed(() => props.private && !isExternalSend.value),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
  sendOrigin,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full message-bubble-container group/msg"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      // SiteUp V2 (G4): gap 8px entre bolhas (mb-2); agrupadas ficam mais juntas
      shouldGroupWithNext ? 'mb-0.5' : 'mb-2',
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] relative flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
        <!-- SiteUp: origem do envio como selo no avatar (tooltip explica) -->
        <span
          v-if="sendOrigin"
          v-tooltip.left="
            sendOrigin === 'mobile'
              ? t('CONVERSATION.ORIGIN.MOBILE_TITLE')
              : t('CONVERSATION.ORIGIN.API_TITLE')
          "
          class="absolute -bottom-1 ltr:-right-1 rtl:-left-1 flex size-3.5 items-center justify-center rounded-full bg-n-solid-3 text-n-slate-11 ring-2 ring-n-background"
        >
          <span
            class="size-2.5"
            :class="
              sendOrigin === 'mobile'
                ? 'i-ri-smartphone-line'
                : 'i-ri-flashlight-line'
            "
          />
        </span>
      </div>
      <div
        class="[grid-area:bubble] flex min-w-0 items-center gap-1.5"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <!-- SiteUp: atalhos de responder, reagir e encaminhar. No mouse
             somem e voltam no hover da linha (group/msg). No touch
             ficam visíveis. Encaminhar reusa o modal do menu. -->
        <button
          v-if="contextMenuEnabledOptions.replyTo"
          v-tooltip.top="t('CONVERSATION.CONTEXT_MENU.REPLY_TO')"
          type="button"
          :aria-label="t('CONVERSATION.CONTEXT_MENU.REPLY_TO')"
          :class="[
            quickActionButtonClass,
            quickActionOrderClass,
            showContextMenu ? forceVisibleClass : '',
          ]"
          @click.stop="handleReplyTo"
        >
          <span class="i-ph-arrow-bend-up-left size-5" />
        </button>
        <MessageReactionPicker
          v-if="canReact"
          :selected="agentReactionEmoji"
          :disabled="isReacting"
          :hover-reveal-class="hoverRevealClass"
          :force-visible="showContextMenu"
          :class="quickActionOrderClass"
          @select="sendReaction"
        />
        <button
          v-if="
            !isWorkspaceV2Path(route?.path) &&
            contextMenuEnabledOptions.forward &&
            shouldShowContextMenu
          "
          v-tooltip.top="t('CONVERSATION.CONTEXT_MENU.FORWARD')"
          type="button"
          :aria-label="t('CONVERSATION.CONTEXT_MENU.FORWARD')"
          :class="[
            quickActionButtonClass,
            quickActionOrderClass,
            showContextMenu ? forceVisibleClass : '',
          ]"
          @click.stop="openForwardModal"
        >
          <span class="i-ri-share-forward-line size-5" />
        </button>
        <div
          class="relative min-w-0"
          :class="{ 'w-full': variant === MESSAGE_VARIANTS.EMAIL }"
        >
          <AiCenterPrivateNote
            v-if="aiCenterNote"
            :item="aiCenterNote"
            :conversation-id="conversationId"
          />
          <Component :is="componentToRender" v-else />
          <KanbanMessageCards
            v-if="
              isWorkspaceV2Path(route?.path) &&
              !isMessageDeleted &&
              isBubble &&
              route.params.accountId &&
              currentUserId
            "
            :content="displayContent || ''"
            :account-id="route.params.accountId"
            :user-id="currentUserId"
          />
          <div
            v-if="shouldShowHoverChevron && chevronFadeClass"
            class="pointer-events-none absolute top-0 h-10 w-11"
            :class="[
              hoverHideOnFinePointerClass,
              chevronCornerClass,
              chevronFadeClass,
              chevronRadiusClass,
              { '!opacity-100': showContextMenu },
            ]"
          />
          <button
            v-if="shouldShowHoverChevron"
            type="button"
            :aria-label="t('CONVERSATION.CONTEXT_MENU.OPEN_ACTIONS')"
            data-testid="message-actions-chevron"
            class="absolute top-0 z-10 flex size-9 items-center justify-center"
            :class="[
              hoverRevealClass,
              chevronCornerClass,
              chevronIconClass,
              chevronButtonSurfaceClass,
              showContextMenu ? forceVisibleClass : '',
            ]"
            @click.stop="openContextMenuFromChevron"
          >
            <span class="i-ri-arrow-down-s-fill size-5" />
          </button>
        </div>
      </div>
      <!-- SiteUp V2 (F1.7): rodapé da mensagem, FORA da bolha. Empilha em
           coluna a linha da hora, o erro de envio e o rastro de tools da IA —
           os três dividem a [grid-area:meta] e antes se sobrepunham. -->
      <div
        v-if="showMetaArea"
        class="[grid-area:meta] flex flex-col gap-y-0.5 mt-[3px] px-1"
      >
        <!-- W2: reações da mensagem, acima da linha da hora. -->
        <MessageReactions
          :reactions="reactions"
          :disabled="isReacting"
          :class="flexOrientationClass"
          @toggle="onToggleReaction"
        />
        <!-- M3/M4: selos de apagada/editada moram DENTRO da bolha
             (Base + MessageLifecycle). Aqui só a linha da hora. -->
        <div
          v-if="showMessageMeta"
          class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-n-slate-11"
          :class="flexOrientationClass"
        >
          <MessageMeta />
        </div>
        <MessageError
          v-if="contentAttributes.externalError"
          :class="flexOrientationClass"
          :error="contentAttributes.externalError"
          @retry="emit('retry')"
        />
        <!-- SiteUp (#5): ferramentas que a IA usou nesta resposta (chip discreto, no 1o balao) -->
        <div
          v-if="isAiMessage && aiToolCalls.length"
          class="flex flex-wrap"
          :class="flexOrientationClass"
        >
          <span
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11 text-xxs"
            :title="t('CONVERSATION.AI_TOOLS_USED')"
          >
            <span class="i-ri-wrench-line size-3 text-n-slate-10 shrink-0" />
            {{ aiToolCalls.join(' · ') }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        ref="messageContextMenu"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}
</style>
